import { useEffect, useRef, useState } from "react";

/**
 * Pads a boolean flag so it stays `true` for at least `minMs` once it turns
 * `true`, even if `isActive` flips back to `false` sooner — so a modal driven
 * by a fast request doesn't flash open and shut.
 */
export const useMinDuration = (isActive: boolean, minMs: number): boolean => {
  const [isPadded, setIsPadded] = useState(isActive);
  const becameActiveAt = useRef<number | null>(isActive ? Date.now() : null);

  useEffect(() => {
    if (isActive) {
      becameActiveAt.current = Date.now();
      setIsPadded(true);
      return undefined;
    }

    if (becameActiveAt.current === null) {
      setIsPadded(false);
      return undefined;
    }

    const elapsed = Date.now() - becameActiveAt.current;
    const remaining = Math.max(minMs - elapsed, 0);

    const timeoutId = setTimeout(() => {
      becameActiveAt.current = null;
      setIsPadded(false);
    }, remaining);

    return () => clearTimeout(timeoutId);
  }, [isActive, minMs]);

  return isPadded;
};
